/**
 * Turning raw editor changes into the one number that matters: how much of this
 * code the student actually composed.
 *
 * Kept apart from React and from Monaco's types so the rule can be tested
 * directly. Structural types rather than `monaco.editor.*` imports — this file
 * needs two fields, not a dependency on the editor.
 */

/** The shape of one Monaco content change. */
export interface EditorChange {
  /** Text that was inserted. Empty for a pure deletion. */
  text: string;
  /** Length of the text it replaced. */
  rangeLength: number;
}

/**
 * Insertions longer than this are not someone typing.
 *
 * A keystroke inserts one character; an auto-closed bracket or quote inserts
 * two; an auto-indent on Enter can add a newline plus a few spaces. Four is
 * comfortably above all of those and far below anything that arrives whole.
 */
export const TYPED_CHAR_LIMIT = 4;

/**
 * Characters the student typed, out of a batch of editor changes.
 *
 * Everything larger is excluded rather than counted, which quietly handles two
 * things that would otherwise corrupt the number:
 *
 *   - `formatOnPaste` is on in the editor, so every paste is followed by a
 *     second, large re-indenting edit. Counting it would make a pasted solution
 *     look partly typed — exactly backwards.
 *   - Accepting an autocomplete suggestion inserts a whole identifier or
 *     snippet at once. That is not composition either, and it should not be
 *     credited as such.
 *
 * Deletions contribute nothing. Someone who writes a line and deletes it has
 * still composed it, and the alternative — netting deletions off — would let a
 * student reduce their own typed count by editing, which is absurd.
 */
export function typedCharsFrom(changes: readonly EditorChange[] | undefined): number {
  if (!changes) return 0;
  let typed = 0;
  for (const change of changes) {
    const inserted = change.text?.length ?? 0;
    if (inserted > 0 && inserted <= TYPED_CHAR_LIMIT) {
      typed += inserted;
    }
  }
  return typed;
}
